import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  finalizeOffer,
  getOffer,
  lockField,
  subscribeOfferSockets,
  unlockField,
  updateOfferSocket,
} from "../../actions/offerActions";
import { getProducts } from "../../actions/product.action";
import ReusableMultiSelectDialog from "../../components/ReusableMultiSelectDialog";
import { resolveAnyStaticUrl } from "../../urlConfig";
import { MainContainer } from "../MainContainer";
import RoomPresence from "../Presence/RoomPresence";
import OfferHeaderForm from "./OfferHeaderForm";
import OfferItemsTable from "./OfferItemsTable";
import OfferPreview from "./OfferPreview";
import OfferToolbar from "./OfferToolbar";
import {
  buildOfferJson,
  makeOfferJsonFilename,
  parseOfferJson,
} from "./offerJson";

const EMPTY_CURRENT = Object.freeze({
  data: null,
  loading: false,
  saving: false,
  locking: Object.freeze({}),
  lastPdfUrl: null,
});

const PUBLIC_SITE_ORIGIN = String(
  process.env.REACT_APP_PUBLIC_SITE_ORIGIN || "https://alotb.ru"
).replace(/\/+$/, "");

const getProductImage = (product) => {
  const pictures = Array.isArray(product?.productPictures)
    ? product.productPictures
    : [];
  const picture = pictures.find((item) => item?.mainImg) || pictures[0];

  if (picture?.img) {
    return `/public/${String(picture.img).replace(/^files[\\/]/, "")}`;
  }

  return product?.reducedImage || null;
};

const getProductDimensions = (product) =>
  [product?.length, product?.width, product?.height]
    .filter((value) => value !== undefined && value !== null && value !== "")
    .join(" × ");

const downloadJson = (payload, filename) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export default function OfferEditor() {
  const { id: offerId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const importInputRef = useRef(null);

  const authUserId = useSelector((state) => state.auth?.user?._id);
  const current = useSelector(
    (state) => state.offers?.current || EMPTY_CURRENT
  );
  const productState = useSelector((state) => state.product || {});

  const allProducts = useMemo(() => {
    const fullList = Array.isArray(productState.allProducts)
      ? productState.allProducts
      : [];

    if (fullList.length) return fullList;

    return Array.isArray(productState.products)
      ? productState.products
      : [];
  }, [productState.allProducts, productState.products]);

  const productsLoading = Boolean(
    productState.loading ||
      productState.loadingProducts ||
      productState.getAllProductsLoading
  );

  const {
    data: offer,
    loading,
    saving,
    locking,
    lastPdfUrl,
  } = current;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedIds, setPickedIds] = useState([]);
  const [pickerContext, setPickerContext] = useState({
    mode: "append",
    itemIndex: null,
  });
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!offerId) return undefined;

    dispatch(getOffer(offerId));
    const unsubscribe = dispatch(
      subscribeOfferSockets(offerId, authUserId)
    );

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [offerId, authUserId, dispatch]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 7000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (!pickerOpen) return undefined;

    dispatch(getProducts("createdAtMinus", 1, 2000));

    return undefined;
  }, [pickerOpen, dispatch]);

  const resolvedPdfUrl = useMemo(() => {
    const source = lastPdfUrl || offer?.pdfs?.slice(-1)[0]?.url;
    return source ? resolveAnyStaticUrl(source) : "";
  }, [lastPdfUrl, offer?.pdfs]);

  const onHeaderChange = async (patch) => {
    const response = await dispatch(
      updateOfferSocket(offerId, patch, authUserId)
    );

    if (response && response.ok === false) {
      setNotice({
        type: "error",
        text: response.error || "Не удалось сохранить изменения.",
      });
    }

    return response;
  };

  const onItemsChange = async (items) => {
    const response = await dispatch(
      updateOfferSocket(offerId, { items }, authUserId)
    );

    if (response && response.ok === false) {
      setNotice({
        type: "error",
        text: response.error || "Не удалось сохранить позиции.",
      });
    }

    return response;
  };

  const onLock = (fieldPath) => {
    dispatch(lockField(offerId, fieldPath, authUserId));
  };

  const onUnlock = (fieldPath) => {
    dispatch(unlockField(offerId, fieldPath));
  };

  const doFinalize = async () => {
    setNotice(null);

    if (typeof document !== "undefined" && document.activeElement) {
      document.activeElement.blur();
    }

    // Даём последнему onBlur сохранить поле до формирования PDF.
    await new Promise((resolve) => setTimeout(resolve, 220));

    const result = await dispatch(finalizeOffer(offerId, authUserId));

    if (result?.url) {
      window.open(
        resolveAnyStaticUrl(result.url),
        "_blank",
        "noopener,noreferrer"
      );
      setNotice({
        type: "success",
        text: "PDF сформирован и сохранён в истории предложения.",
      });
      return;
    }

    setNotice({
      type: "error",
      text:
        result?.error ||
        "Не удалось сформировать PDF. Проверьте сообщение сервера.",
    });
  };

  const buildItemFromProduct = (product) => {
    const slug = product?.slug ? String(product.slug) : "";
    const productUrl = slug
      ? `${PUBLIC_SITE_ORIGIN}/product/${encodeURIComponent(slug)}`
      : "";

    return {
      productId: product._id,
      name: product.fullName || product.name || "Товар",
      qty: 1,
      price: typeof product.price === "number" ? product.price : 0,
      sum: 0,
      imageUrl: getProductImage(product),
      meta: {
        unit: "шт.",
        details: product.description || "",
        dimensions: getProductDimensions(product),
        showImage: true,
        productUrl,
        showProductLink: Boolean(productUrl),
        sceneUrl: "",
        showSceneLink: false,
        catalogProduct: {
          productId: String(product._id || ""),
          slug,
          article: String(product.article || ""),
          query: product.fullName || product.name || "",
          attachLater: false,
        },
      },
    };
  };

  const attachProductToItem = (item, product) => {
    const productSnapshot = buildItemFromProduct(product);
    const currentMeta = item?.meta || {};

    return {
      ...item,
      productId: product._id,
      name: item?.name || productSnapshot.name,
      price:
        Number(item?.price || 0) > 0
          ? Number(item.price)
          : productSnapshot.price,
      imageUrl: productSnapshot.imageUrl,
      meta: {
        ...currentMeta,
        unit: currentMeta.unit || productSnapshot.meta.unit,
        details: currentMeta.details || productSnapshot.meta.details,
        dimensions:
          currentMeta.dimensions || productSnapshot.meta.dimensions,
        showImage:
          typeof currentMeta.showImage === "boolean"
            ? currentMeta.showImage
            : true,
        productUrl: productSnapshot.meta.productUrl,
        showProductLink:
          typeof currentMeta.showProductLink === "boolean"
            ? currentMeta.showProductLink
            : Boolean(productSnapshot.meta.productUrl),
        sceneUrl: currentMeta.sceneUrl || "",
        showSceneLink: Boolean(currentMeta.showSceneLink),
        catalogProduct: productSnapshot.meta.catalogProduct,
      },
    };
  };

  const openAppendPicker = () => {
    setPickerContext({ mode: "append", itemIndex: null });
    setPickedIds([]);
    setPickerOpen(true);
  };

  const openAttachPicker = (itemIndex) => {
    setPickerContext({ mode: "attach", itemIndex });
    setPickedIds([]);
    setPickerOpen(true);
  };

  const applyPickedProducts = async () => {
    if (!pickedIds?.length) {
      setPickerOpen(false);
      return;
    }

    const productsById = new Map(
      (allProducts || []).map((product) => [
        String(product._id),
        product,
      ])
    );

    if (pickerContext.mode === "attach") {
      const product = productsById.get(String(pickedIds[0]));
      const index = Number(pickerContext.itemIndex);

      if (!product || !Number.isInteger(index) || index < 0) {
        setNotice({
          type: "error",
          text: "Не удалось определить позицию или выбранный товар.",
        });
        return;
      }

      const nextItems = (offer?.items || []).map((item, itemIndex) =>
        itemIndex === index ? attachProductToItem(item, product) : item
      );

      const response = await onItemsChange(nextItems);
      if (!response || response.ok !== false) {
        setNotice({
          type: "success",
          text: "Товар привязан. Фото и ссылка взяты из каталога.",
        });
      }
    } else {
      const newItems = pickedIds
        .map((id) => productsById.get(String(id)))
        .filter(Boolean)
        .map(buildItemFromProduct);

      await onItemsChange([...(offer?.items || []), ...newItems]);
    }

    setPickerOpen(false);
    setPickedIds([]);
  };

  const exportJson = () => {
    try {
      downloadJson(buildOfferJson(offer), makeOfferJsonFilename(offer));
      setNotice({
        type: "success",
        text: "JSON выгружен. Его можно редактировать или передать мне для заполнения.",
      });
    } catch (error) {
      setNotice({
        type: "error",
        text: `Не удалось экспортировать JSON: ${error.message}`,
      });
    }
  };

  const requestJsonImport = () => {
    if (importInputRef.current) {
      importInputRef.current.value = "";
      importInputRef.current.click();
    }
  };

  const importJson = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setNotice({
        type: "error",
        text: "JSON слишком большой. Максимальный размер — 2 МБ.",
      });
      return;
    }

    setImporting(true);
    setNotice(null);

    try {
      const patch = parseOfferJson(await file.text());
      const itemsCount = patch.items?.length || 0;
      const confirmed = window.confirm(
        `Импорт заменит данные и позиции текущего КП. Позиций в файле: ${itemsCount}. Продолжить?`
      );

      if (!confirmed) return;

      const response = await dispatch(
        updateOfferSocket(offerId, patch, authUserId)
      );

      if (!response?.ok) {
        throw new Error(response?.error || "Сервер не принял импорт.");
      }

      setNotice({
        type: "success",
        text:
          "JSON импортирован. Позиции без каталожного товара можно привязать позже отдельной кнопкой.",
      });
    } catch (error) {
      setNotice({
        type: "error",
        text: `Импорт не выполнен: ${error.message}`,
      });
    } finally {
      setImporting(false);
      event.target.value = "";
    }
  };

  if (loading || !offer) {
    return (
      <div className="offer-editor__loader">
        <span className="offer-editor__loader-dot" />
        Загрузка коммерческого предложения…
      </div>
    );
  }

  return (
    <MainContainer
      backButtonProduct
      type="minimalFunctional"
      href="/admin/offers"
      title="Редактор коммерческого предложения"
    >
      <div className="offer-editor">
        <input
          ref={importInputRef}
          className="offer-editor__hidden-input"
          type="file"
          accept="application/json,.json"
          onChange={importJson}
        />

        <div className="offer-editor__intro">
          <div>
            <span className="offer-editor__eyebrow">
              Коммерческое предложение
            </span>
            <h2>{offer.title || "Новое предложение"}</h2>
            <p>
              Заполните данные слева. Справа отображается предварительный
              вид документа, близкий к итоговому PDF.
            </p>
          </div>

          <div className="offer-editor__intro-meta">
            <span
              className={`offer-editor__status offer-editor__status--${
                offer.status || "draft"
              }`}
            >
              {offer.status === "final" ? "PDF сформирован" : "Черновик"}
            </span>
            {offer.docNumber && (
              <span className="offer-editor__document-number">
                № {offer.docNumber}
              </span>
            )}
          </div>
        </div>

        {notice && (
          <div
            className={`offer-editor__notice offer-editor__notice--${notice.type}`}
            role="status"
          >
            <span>{notice.type === "error" ? "!" : "✓"}</span>
            <p>{notice.text}</p>
            <button type="button" onClick={() => setNotice(null)}>
              ×
            </button>
          </div>
        )}

        {offerId && <RoomPresence roomId={`offer:${offerId}`} />}

        <div className="offer-editor__workspace">
          <div className="offer-editor__form-column">
            <section className="offer-editor__panel">
              <OfferHeaderForm
                offer={offer}
                locking={locking}
                onChange={onHeaderChange}
                onLock={onLock}
                onUnlock={onUnlock}
              />
            </section>

            <section className="offer-editor__panel">
              <OfferItemsTable
                items={offer.items || []}
                currency={offer.currency}
                locking={locking}
                onChange={onItemsChange}
                onLock={onLock}
                onUnlock={onUnlock}
                onOpenPicker={openAppendPicker}
                onAttachProduct={openAttachPicker}
              />
            </section>
          </div>

          <aside className="offer-editor__preview-column">
            <OfferPreview offer={offer} />
          </aside>
        </div>

        <OfferToolbar
          totals={offer.totals}
          currency={offer.currency}
          saving={saving}
          importing={importing}
          status={offer.status}
          lastPdfUrl={resolvedPdfUrl}
          onFinalize={doFinalize}
          onImportJson={requestJsonImport}
          onExportJson={exportJson}
          back={() => navigate("/admin/offers")}
        />

        <ReusableMultiSelectDialog
          open={pickerOpen}
          onClose={() => {
            setPickerOpen(false);
            setPickedIds([]);
          }}
          hideTrigger
          handleClickBottomVisible
          type="product"
          selectionMode={
            pickerContext.mode === "attach" ? "single" : "multiple"
          }
          title={
            pickerContext.mode === "attach"
              ? "Привязать товар к позиции КП"
              : "Добавить товары в КП"
          }
          description={
            pickerContext.mode === "attach"
              ? "Выберите один товар. Название и цена из JSON сохранятся, а фото и ссылка подтянутся из каталога."
              : "Найдите нужные товары по названию, артикулу, категории или характеристикам."
          }
          data={allProducts}
          loading={productsLoading && !allProducts.length}
          selectedValues={pickedIds}
          setSelectedValues={setPickedIds}
          onSubmit={applyPickedProducts}
          buttonText={
            pickerContext.mode === "attach"
              ? "Привязать выбранный товар"
              : "Добавить выбранные товары"
          }
          inputLabelText="товар"
          searchPlaceholder="Название, артикул, категория, размеры…"
          pageSize={24}
        />
      </div>
    </MainContainer>
  );
}
